<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key =>
  t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.CONNECTORS.${key}`);

const connectors = ref([]);
const showForm = ref(false);
const name = ref('');
const description = ref('');
const baseUrl = ref('');
const authType = ref('NONE');
const apiKeyFieldName = ref('Authorization');
const apiKeyValue = ref('');
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchConnectors = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listConnectors(props.hookId);
    connectors.value = data;
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

const bundles = ref([]);
const activatingBundleKey = ref(null);

const fetchBundles = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listConnectorBundles(
      props.hookId
    );
    bundles.value = data;
  } catch (error) {
    bundles.value = [];
  }
};

const activateBundle = async bundle => {
  activatingBundleKey.value = bundle.key;
  try {
    await MetaBusinessAgentAPI.activateConnectorBundle(
      props.hookId,
      bundle.key
    );
    useAlert(
      t(
        'INTEGRATION_SETTINGS.META_BUSINESS_AGENT.CONNECTORS.API.BUNDLE_ACTIVATE_SUCCESS',
        { name: bundle.name }
      )
    );
    fetchConnectors();
  } catch (error) {
    useAlert(
      t(
        'INTEGRATION_SETTINGS.META_BUSINESS_AGENT.CONNECTORS.API.BUNDLE_ACTIVATE_ERROR',
        { name: bundle.name }
      )
    );
  } finally {
    activatingBundleKey.value = null;
  }
};

onMounted(() => {
  fetchConnectors();
  fetchBundles();
});

const resetForm = () => {
  name.value = '';
  description.value = '';
  baseUrl.value = '';
  authType.value = 'NONE';
  apiKeyFieldName.value = 'Authorization';
  apiKeyValue.value = '';
  showForm.value = false;
};

const createConnector = async () => {
  try {
    const { data: connector } = await MetaBusinessAgentAPI.createConnector(
      props.hookId,
      {
        name: name.value,
        description: description.value,
        base_url: baseUrl.value,
        auth_type: authType.value,
      }
    );
    if (authType.value === 'API_KEY' && apiKeyValue.value) {
      await MetaBusinessAgentAPI.upsertConnectorApiKey(
        props.hookId,
        connector.id,
        {
          headers: [
            { field_name: apiKeyFieldName.value, value: apiKeyValue.value },
          ],
        }
      );
    }
    useAlert(T('API.CREATE_SUCCESS'));
    resetForm();
    fetchConnectors();
  } catch (error) {
    useAlert(T('API.CREATE_ERROR'));
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteConnector = async () => {
  try {
    await MetaBusinessAgentAPI.deleteConnector(
      props.hookId,
      pendingDelete.value.id
    );
    useAlert(T('API.DELETE_SUCCESS'));
    fetchConnectors();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="bundles.length" class="flex flex-col gap-2">
      <p class="text-sm text-n-slate-11 m-0">{{ T('BUNDLES_TITLE') }}</p>
      <div class="flex flex-wrap gap-2">
        <Button
          v-for="bundle in bundles"
          :key="bundle.key"
          slate
          faded
          :label="
            t(
              'INTEGRATION_SETTINGS.META_BUSINESS_AGENT.CONNECTORS.BUNDLE_ACTIVATE_BUTTON',
              { name: bundle.name }
            )
          "
          :is-loading="activatingBundleKey === bundle.key"
          @click="activateBundle(bundle)"
        />
      </div>
    </div>
    <div class="flex justify-end">
      <Button :label="T('ADD_BUTTON')" @click="showForm = true" />
    </div>
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <Input
        v-model="name"
        :label="T('NAME_LABEL')"
        placeholder="Shopify Order Management"
      />
      <TextArea
        v-model="description"
        :label="T('DESCRIPTION_LABEL')"
        :max-length="1000"
      />
      <Input
        v-model="baseUrl"
        :label="T('BASE_URL_LABEL')"
        placeholder="https://api.example.com"
      />
      <div>
        <label class="text-sm text-n-slate-11">{{
          T('AUTH_TYPE_LABEL')
        }}</label>
        <select v-model="authType">
          <option value="NONE">{{ T('AUTH_TYPE_NONE') }}</option>
          <option value="API_KEY">{{ T('AUTH_TYPE_API_KEY') }}</option>
        </select>
      </div>
      <template v-if="authType === 'API_KEY'">
        <Input v-model="apiKeyFieldName" :label="T('API_KEY_FIELD_LABEL')" />
        <Input
          v-model="apiKeyValue"
          :label="T('API_KEY_LABEL')"
          type="password"
        />
      </template>
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click="resetForm"
        />
        <Button
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          @click="createConnector"
        />
      </div>
    </div>
    <ResourceList
      :items="connectors"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">{{ item.name }}</p>
        <p class="text-sm text-n-slate-11 m-0">{{ item.base_url }}</p>
        <p class="text-xs text-n-slate-10 m-0">
          {{ T('STATUS_LABEL') }}: {{ item.connection_status?.status }}
        </p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteConnector"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
